import { create } from 'zustand';
import type {
  Notification,
  NotificationOptions,
  NotificationState,
  NotificationActions
} from '@/shared/types/notification';

/**
 * Длительность уведомлений по умолчанию (в миллисекундах)
 */
const DEFAULT_DURATIONS = {
  success: 3000,
  error: 5000,
  warning: 4000,
  info: 4000
} as const;

/**
 * Максимальное количество одновременно отображаемых уведомлений
 */
const MAX_NOTIFICATIONS_COUNT = 5;

/**
 * Объединенный тип состояния и действий для уведомлений
 */
type NotificationStore = NotificationState & NotificationActions;

/**
 * Генерация уникального ID для уведомления
 */
const generateId = (): string => {
  return `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Zustand store для управления уведомлениями
 * Предоставляет централизованную систему snackbar уведомлений
 */
export const useNotificationStore = create<NotificationStore>((set, get) => ({
  notifications: [],

  /**
   * Добавить новое уведомление
   * @param options - Опции уведомления
   * @returns ID созданного уведомления
   */
  addNotification: (options: NotificationOptions): string => {
    const id = generateId();
    const duration = options.duration ?? DEFAULT_DURATIONS[options.type];

    const notification: Notification = {
      id,
      type: options.type,
      ...(options.title !== undefined && { title: options.title }),
      message: options.message,
      duration,
      dismissible: options.dismissible ?? true,
      createdAt: Date.now()
    };

    set((state) => {
      const newNotifications = [notification, ...state.notifications];

      // Ограничиваем количество уведомлений
      if (newNotifications.length > MAX_NOTIFICATIONS_COUNT) {
        newNotifications.splice(MAX_NOTIFICATIONS_COUNT);
      }

      return { notifications: newNotifications };
    });

    // Автоматическое удаление уведомления через заданное время
    if (duration > 0) {
      setTimeout(() => {
        get().removeNotification(id);
      }, duration);
    }

    return id;
  },

  /**
   * Удалить уведомление по ID
   * @param id - ID уведомления
   */
  removeNotification: (id: string): void => {
    set((state) => ({
      notifications: state.notifications.filter(
        (notification) => notification.id !== id
      )
    }));
  },

  /**
   * Очистить все уведомления
   */
  clearAllNotifications: (): void => {
    set({ notifications: [] });
  },

  /**
   * Показать уведомление об успехе
   * @param message - Текст сообщения
   * @param title - Заголовок (опционально)
   * @param duration - Длительность отображения (опционально)
   * @returns ID созданного уведомления
   */
  showSuccess: (message: string, title?: string, duration?: number): string => {
    return get().addNotification({
      type: 'success',
      message,
      ...(title !== undefined && { title }),
      ...(duration !== undefined && { duration })
    });
  },

  /**
   * Показать уведомление об ошибке
   * @param message - Текст сообщения
   * @param title - Заголовок (опционально)
   * @param duration - Длительность отображения (опционально)
   * @returns ID созданного уведомления
   */
  showError: (message: string, title?: string, duration?: number): string => {
    return get().addNotification({
      type: 'error',
      message,
      ...(title !== undefined && { title }),
      ...(duration !== undefined && { duration })
    });
  },

  /**
   * Показать предупреждение
   * @param message - Текст сообщения
   * @param title - Заголовок (опционально)
   * @param duration - Длительность отображения (опционально)
   * @returns ID созданного уведомления
   */
  showWarning: (message: string, title?: string, duration?: number): string => {
    return get().addNotification({
      type: 'warning',
      message,
      ...(title !== undefined && { title }),
      ...(duration !== undefined && { duration })
    });
  },

  /**
   * Показать информационное уведомление
   * @param message - Текст сообщения
   * @param title - Заголовок (опционально)
   * @param duration - Длительность отображения (опционально)
   * @returns ID созданного уведомления
   */
  showInfo: (message: string, title?: string, duration?: number): string => {
    return get().addNotification({
      type: 'info',
      message,
      ...(title !== undefined && { title }),
      ...(duration !== undefined && { duration })
    });
  }
}));

/**
 * Хуки для удобного использования уведомлений
 */
export const useNotifications = () => {
  const notifications = useNotificationStore((state) => state.notifications);
  return notifications;
};

/**
 * Хук для получения только состояния уведомлений
 */
export const useNotificationState = () => {
  const notifications = useNotificationStore((state) => state.notifications);
  return { notifications };
};

export const useNotificationActions = () => {
  const {
    addNotification,
    removeNotification,
    clearAllNotifications,
    showSuccess,
    showError,
    showWarning,
    showInfo
  } = useNotificationStore();

  return {
    addNotification,
    removeNotification,
    clearAllNotifications,
    showSuccess,
    showError,
    showWarning,
    showInfo
  };
};
